import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { marked } from 'marked';
import type { Diagram, ProjectVersion, Threat, Entity, Boundary } from '@/types';
import { calculateDiagramStats, createBoundaryMap, createEntityMap, getBoundaryChain } from '@/lib/indexers';
import { entityLabel } from '@/lib/diagramFields';
import { getCiaLabel } from '@/lib/constants';
import { threatSeverity, type RatingSeverity } from '@/lib/owaspRating';

interface ExportPdfParams {
  isProject: boolean;
  projectInfo: ProjectVersion | null;
  diagrams: Diagram[];
  currentDiagramIndex: number;
  currentDiagram: Diagram | null;
  threats: Threat[];
  fileName: string | null;
  fileSize: number | null;
}

interface JsPdfWithAutoTable extends jsPDF {
  lastAutoTable?: {
    finalY?: number;
  };
}

const typeLabels: Record<string, string> = {
  default: 'Default',
  external: 'External System',
  user: 'User',
  process: 'Process',
  storage: 'Storage System',
};

type CiaColor = [number, number, number];

const ciaColors: Record<string, { bg: CiaColor; text: CiaColor; border: CiaColor }> = {
  // Confidentiality
  confidential: { bg: [254, 226, 226], text: [153, 27, 27], border: [252, 165, 165] },
  secret:       { bg: [243, 232, 255], text: [107, 33, 168], border: [216, 180, 254] },
  internal:     { bg: [254, 243, 199], text: [146, 64, 14], border: [253, 224, 132] },
  public:       { bg: [220, 252, 231], text: [22, 101, 52],  border: [134, 239, 172] },
  // Integrity / Availability
  standard:     { bg: [219, 234, 254], text: [30, 64, 175],  border: [147, 197, 253] },
  high:         { bg: [255, 237, 213], text: [154, 52, 18],  border: [253, 186, 116] },
  critical:     { bg: [254, 226, 226], text: [153, 27, 27],  border: [252, 165, 165] },
  highly_critical: { bg: [243, 232, 255], text: [107, 33, 168], border: [216, 180, 254] },
  low:          { bg: [220, 252, 231], text: [22, 101, 52],  border: [134, 239, 172] },
  '—':          { bg: [241, 245, 249], text: [100, 116, 139], border: [203, 213, 225] },
};

/** OWASP Risk Rating severity shown in the Threats table. */
export function pdfSeverityLabel(threat: Threat): string {
  return threatSeverity(threat.rating_vector_string, threat.type).toUpperCase();
}

export function pdfSeverityCounts(threats: Threat[]): Partial<Record<RatingSeverity, number>> {
  return threats.reduce((acc, t) => {
    const severity = threatSeverity(t.rating_vector_string, t.type);
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {} as Partial<Record<RatingSeverity, number>>);
}

// accent brand color
const BRAND: CiaColor = [30, 41, 59];      // slate-800
const ACCENT: CiaColor = [59, 130, 246];   // blue-500
const MUTED: CiaColor = [100, 116, 139];   // slate-500

function sanitizeFilename(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '') || 'project';
}

function toLines(value: string, maxWidth: number, doc: jsPDF): string[] {
  return doc.splitTextToSize(value || '-', maxWidth) as string[];
}

class PdfWriter {
  private doc: jsPDF;
  private y: number;
  // top margin leaves room for header chrome (added in post-processing)
  readonly margin = 14;
  readonly headerH = 14; // reserve space at top for chrome

  constructor(doc: jsPDF) {
    this.doc = doc;
    this.y = this.headerH + 2;
  }

  // ─── Typography ────────────────────────────────────────────────────────

  addSectionHeader(text: string, subtitle?: string) {
    // Always starts on a fresh page
    const pageW = this.doc.internal.pageSize.getWidth();

    // accent bar full width
    this.doc.setFillColor(...BRAND);
    this.doc.rect(0, this.y - 1, pageW, 10, 'F');

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(12);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(text.toUpperCase(), this.margin, this.y + 6);

    this.doc.setTextColor(0, 0, 0);
    this.y += 13;

    if (subtitle) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(9);
      this.doc.setTextColor(...MUTED);
      this.doc.text(subtitle, this.margin, this.y);
      this.doc.setTextColor(0, 0, 0);
      this.y += 6;
    }
  }

  addSubsection(text: string) {
    this.ensureSpace(12);
    this.y += 2;
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(10);
    this.doc.setTextColor(...BRAND);
    this.doc.text(text, this.margin, this.y);
    this.doc.setTextColor(0, 0, 0);
    this.y += 6;
  }

  addCaption(text: string) {
    this.ensureSpace(7);
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(8);
    this.doc.setTextColor(...MUTED);
    this.doc.text(text, this.margin, this.y);
    this.doc.setTextColor(0, 0, 0);
    this.y += 5;
  }

  addParagraph(text: string) {
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    const lines = toLines(text, this.getContentWidth(), this.doc);
    this.ensureSpace(lines.length * 5 + 2);
    for (const line of lines) {
      this.doc.text(line, this.margin, this.y);
      this.y += 5;
    }
    this.y += 1;
  }

  addBullet(text: string, color: CiaColor = MUTED) {
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    const bullet = '• ';
    const bw = this.doc.getTextWidth(bullet);
    const lines = toLines(text, this.getContentWidth() - bw, this.doc);
    this.ensureSpace(lines.length * 5 + 2);
    this.doc.setTextColor(...color);
    this.doc.text(bullet, this.margin, this.y);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(lines[0] || '-', this.margin + bw, this.y);
    this.y += 5;
    for (let i = 1; i < lines.length; i++) {
      this.doc.text(lines[i], this.margin + bw, this.y);
      this.y += 5;
    }
    this.y += 1;
  }

  // ─── Metric cards: compact, colored ────────────────────────────────────

  addStatRow(cards: Array<{ label: string; value: string; bg?: CiaColor; fg?: CiaColor }>) {
    if (cards.length === 0) return;
    const gap = 3;
    const cardH = 12;
    const w = this.getContentWidth();
    const cardW = (w - (cards.length - 1) * gap) / cards.length;
    this.ensureSpace(cardH + 4);

    cards.forEach((card, i) => {
      const x = this.margin + i * (cardW + gap);
      const y = this.y;
      const bg = card.bg ?? [241, 245, 249] as CiaColor;
      const fg = card.fg ?? BRAND;

      this.doc.setFillColor(...bg);
      this.doc.setDrawColor(203, 213, 225);
      this.doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'FD');

      // Value
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(11);
      this.doc.setTextColor(...fg);
      this.doc.text(card.value, x + cardW / 2, y + 7, { align: 'center' });

      // Label
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(6.5);
      this.doc.setTextColor(...MUTED);
      this.doc.text(card.label.toUpperCase(), x + cardW / 2, y + 11, { align: 'center' });
    });

    this.doc.setTextColor(0, 0, 0);
    this.y += cardH + 5;
  }

  // ─── CIA colored badge cards ────────────────────────────────────────────

  addCiaCards(items: Array<{ label: string; value: string }>) {
    const gap = 4;
    const cardH = 14;
    const w = this.getContentWidth();
    const columns = 2;
    const cardW = (w - (columns - 1) * gap) / columns;
    const rowsCount = Math.ceil(items.length / columns);
    this.ensureSpace(rowsCount * (cardH + gap) + 2);

    items.forEach((item, i) => {
      const row = Math.floor(i / columns);
      const col = i % columns;
      const x = this.margin + col * (cardW + gap);
      const y = this.y + row * (cardH + gap);
      const colors = ciaColors[item.value.toLowerCase().replaceAll(' ', '_')] ?? ciaColors.standard;

      this.doc.setFillColor(...colors.bg);
      this.doc.setDrawColor(...colors.border);
      this.doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'FD');

      // label
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(7.5);
      this.doc.setTextColor(...MUTED);
      this.doc.text(item.label.toUpperCase(), x + 4, y + 5);

      // value
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(10);
      this.doc.setTextColor(...colors.text);
      this.doc.text(item.value, x + 4, y + 11);
    });

    this.doc.setTextColor(0, 0, 0);
    this.y += rowsCount * (cardH + gap) + 2;
  }

  // ─── Page control ───────────────────────────────────────────────────────

  newSection(orientation: 'portrait' | 'landscape' = 'portrait') {
    this.doc.addPage('a4', orientation);
    this.y = this.headerH + 2;
  }

  addPage(orientation: 'portrait' | 'landscape' = 'portrait') {
    this.doc.addPage('a4', orientation);
    this.y = this.headerH + 2;
  }

  ensureSpace(requiredHeight: number) {
    const pageHeight = this.doc.internal.pageSize.getHeight();
    if (this.y + requiredHeight <= pageHeight - this.margin) return;
    this.addPage();
  }

  // ─── Accessors ──────────────────────────────────────────────────────────

  getDoc() { return this.doc; }
  getY() { return this.y; }
  setY(v: number) { this.y = v; }
  getMargin() { return this.margin; }
  getContentWidth() {
    return this.doc.internal.pageSize.getWidth() - this.margin * 2;
  }
}

function renderStyledTable(
  doc: jsPDF,
  writer: PdfWriter,
  head: string[][],
  body: Array<Array<string | number>>,
  options?: {
    fontSize?: number;
    columnStyles?: Record<number, { cellWidth?: number }>;
    headFill?: CiaColor;
  }
) {
  const headFill = options?.headFill ?? BRAND;
  autoTable(doc, {
    startY: writer.getY() + 1,
    head,
    body,
    margin: { left: writer.getMargin(), right: writer.getMargin() },
    theme: 'grid',
    styles: {
      fontSize: options?.fontSize ?? 8,
      cellPadding: 1.6,
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [203, 213, 225],
      lineWidth: 0.2,
      textColor: [30, 41, 59],
    },
    headStyles: {
      fillColor: headFill,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: (options?.fontSize ?? 8),
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: options?.columnStyles,
    didDrawPage: () => {
      // reset writer Y after autotable adds pages internally
    },
  });

  const finalY = (doc as JsPdfWithAutoTable).lastAutoTable?.finalY;
  writer.setY((finalY ?? writer.getY()) + 5);
}

// ─── Markdown → element-by-element rendering (no canvas) ────────────────────

type MdElement =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[]; ordered: boolean }
  | { kind: 'blockquote'; text: string }
  | { kind: 'rule' }
  | { kind: 'code_block'; text: string }
  | { kind: 'table'; headers: string[]; rows: string[][] };

// Render a text string that may contain **bold** and `code` segments inline.
// Returns the final Y position after rendering the full text block.
function renderInlineText(
  doc: jsPDF,
  rawText: string,
  x: number,
  y: number,
  maxWidth: number,
  baseFontSize = 9,
  baseTextColor: CiaColor = [30, 41, 59]
): number {
  // Tokenize into segments: { text, bold, code }
  type Segment = { text: string; bold: boolean; code: boolean; italic: boolean };

  const segments: Segment[] = [];
  // Split by **bold** or `code`
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(rawText)) !== null) {
    if (match.index > last) {
      segments.push({ text: rawText.slice(last, match.index), bold: false, code: false, italic: false });
    }
    if (match[1] !== undefined) {
      segments.push({ text: match[1], bold: true, code: false, italic: false });
    } else if (match[2] !== undefined) {
      segments.push({ text: match[2], bold: true, code: true, italic: true });
    }
    last = match.index + match[0].length;
  }
  if (last < rawText.length) {
    segments.push({ text: rawText.slice(last), bold: false, code: false, italic: false });
  }

  // Now word-wrap across segments while tracking font changes
  // Build an array of "words" with their style
  type StyledWord = { word: string; bold: boolean; italic: boolean; code: boolean };
  const words: StyledWord[] = [];

  for (const seg of segments) {
    const parts = seg.text.split(/( +)/); // split but keep spaces
    for (const part of parts) {
      if (part === '') continue;
      words.push({ word: part, bold: seg.bold, italic: seg.italic, code: seg.code });
    }
  }

  // Lay out words into lines respecting maxWidth
  type Line = Array<StyledWord>;
  const lines: Line[] = [];
  let currentLine: Line = [];
  let currentLineWidth = 0;

  const getWordWidth = (w: StyledWord): number => {
    doc.setFontSize(baseFontSize);
    if (w.bold && w.italic) doc.setFont('helvetica', 'bolditalic');
    else if (w.bold) doc.setFont('helvetica', 'bold');
    else doc.setFont('helvetica', 'normal');
    return doc.getTextWidth(w.word);
  };

  for (const w of words) {
    const ww = getWordWidth(w);
    if (currentLine.length > 0 && currentLineWidth + ww > maxWidth + 0.5) {
      lines.push(currentLine);
      currentLine = [w];
      currentLineWidth = ww;
    } else {
      currentLine.push(w);
      currentLineWidth += ww;
    }
  }
  if (currentLine.length > 0) lines.push(currentLine);

  // Render each line
  const lineH = baseFontSize * 0.6 + 1.2;
  for (const line of lines) {
    let cx = x;
    for (const w of line) {
      doc.setFontSize(baseFontSize);
      if (w.bold && w.italic) doc.setFont('helvetica', 'bolditalic');
      else if (w.bold) doc.setFont('helvetica', 'bold');
      else doc.setFont('helvetica', 'normal');

      if (w.code) {
        doc.setTextColor(109, 40, 217); // purple for inline code
      } else {
        doc.setTextColor(...baseTextColor);
      }
      doc.text(w.word, cx, y);
      cx += doc.getTextWidth(w.word);
    }
    y += lineH;
  }

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  return y;
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/* eslint-disable @typescript-eslint/no-explicit-any */
async function parseMarkdownElements(markdown: string): Promise<MdElement[]> {
  marked.setOptions({ gfm: true, breaks: true });
  const tokens = marked.lexer(markdown);
  const elements: MdElement[] = [];

  for (const token of tokens) {
    if (token.type === 'heading') {
      // Strip HTML only from headings (inline bold not needed there)
      elements.push({ kind: 'heading', level: (token as any).depth, text: stripHtml(String((token as any).text)) });
    } else if (token.type === 'paragraph') {
      // Preserve ** and ` markers for inline rendering
      elements.push({ kind: 'paragraph', text: stripHtml(String((token as any).text)) });
    } else if (token.type === 'list') {
      const items: string[] = ((token as any).items as any[]).map((item: any) =>
        stripHtml(String(item.text))
      );
      elements.push({ kind: 'list', items, ordered: !!(token as any).ordered });
    } else if (token.type === 'blockquote') {
      const raw = stripHtml(String((token as any).text));
      elements.push({ kind: 'blockquote', text: raw });
    } else if (token.type === 'hr') {
      elements.push({ kind: 'rule' });
    } else if (token.type === 'code') {
      elements.push({ kind: 'code_block', text: String((token as any).text) });
    } else if (token.type === 'table') {
      const t = token as any;
      const headers: string[] = t.header.map((h: any) => stripHtml(String(h.text)));
      const rows: string[][] = t.rows.map((row: any[]) =>
        row.map((cell: any) => stripHtml(String(cell.text)))
      );
      elements.push({ kind: 'table', headers, rows });
    } else if (token.type === 'space') {
      // skip
    }
  }
  return elements;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

async function writeNotesElements(writer: PdfWriter, elements: MdElement[]) {
  const doc = writer.getDoc();
  const m = writer.getMargin();
  const cw = writer.getContentWidth();

  for (const el of elements) {
    switch (el.kind) {
      case 'heading': {
        const sizes: Record<number, number> = { 1: 14, 2: 12, 3: 10, 4: 9 };
        const sz = sizes[el.level] ?? 10;
        writer.ensureSpace(sz + 5);
        if (el.level === 1) {
          writer.getDoc().setFillColor(241, 245, 249);
          writer.getDoc().rect(m - 1, writer.getY() - 1, cw + 2, sz + 4, 'F');
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(sz);
        doc.setTextColor(...BRAND);
        doc.text(el.text, m, writer.getY() + sz - 2);
        doc.setTextColor(0, 0, 0);
        if (el.level <= 2) {
          doc.setDrawColor(...(ACCENT as [number, number, number]));
          doc.setLineWidth(0.4);
          doc.line(m, writer.getY() + sz + 1, m + cw, writer.getY() + sz + 1);
          doc.setLineWidth(0.2);
        }
        writer.setY(writer.getY() + sz + 5);
        break;
      }

      case 'paragraph': {
        // Estimate height conservatively (may have bold/code segments)
        const plainLen = el.text.replace(/\*\*([^*]+)\*\*|`([^`]+)`/g, '$1$2').length;
        const approxLines = Math.ceil(plainLen / (cw / 5.5)) + 1;
        writer.ensureSpace(approxLines * 6 + 3);
        const finalY = renderInlineText(doc, el.text, m, writer.getY(), cw);
        writer.setY(finalY + 2);
        break;
      }

      case 'list': {
        for (let i = 0; i < el.items.length; i++) {
          const prefix = el.ordered ? `${i + 1}. ` : '• ';
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          const pw = doc.getTextWidth(prefix) + 1;
          // Estimate height
          const plainItem = el.items[i].replace(/\*\*([^*]+)\*\*|`([^`]+)`/g, '$1$2');
          const approxLines = Math.ceil(plainItem.length / ((cw - pw - 2) / 5)) + 1;
          writer.ensureSpace(approxLines * 6 + 2);
          doc.setTextColor(...ACCENT);
          doc.text(prefix, m + 2, writer.getY());
          doc.setTextColor(0, 0, 0);
          const itemFinalY = renderInlineText(doc, el.items[i], m + 2 + pw, writer.getY(), cw - pw - 4);
          writer.setY(itemFinalY);
        }
        doc.setTextColor(0, 0, 0);
        writer.setY(writer.getY() + 2);
        break;
      }

      case 'blockquote': {
        const lines = toLines(el.text, cw - 8, doc);
        writer.ensureSpace(lines.length * 5 + 6);
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.8);
        doc.rect(m, writer.getY() - 2, cw, lines.length * 5 + 4, 'F');
        doc.line(m, writer.getY() - 2, m, writer.getY() + lines.length * 5 + 2);
        doc.setLineWidth(0.2);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        for (const line of lines) {
          doc.text(line, m + 4, writer.getY());
          writer.setY(writer.getY() + 5);
        }
        doc.setTextColor(0, 0, 0);
        writer.setY(writer.getY() + 3);
        break;
      }

      case 'rule': {
        writer.ensureSpace(6);
        doc.setDrawColor(203, 213, 225);
        doc.line(m, writer.getY(), m + cw, writer.getY());
        writer.setY(writer.getY() + 5);
        break;
      }

      case 'code_block': {
        const lines = el.text.split('\n');
        const blockH = lines.length * 5 + 8;
        writer.ensureSpace(blockH);
        doc.setFillColor(15, 23, 42);
        doc.roundedRect(m, writer.getY() - 1, cw, blockH, 1.5, 1.5, 'F');
        doc.setFont('courier', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(226, 232, 240);
        for (const line of lines) {
          doc.text(line, m + 3, writer.getY() + 4);
          writer.setY(writer.getY() + 5);
        }
        doc.setTextColor(0, 0, 0);
        writer.setY(writer.getY() + 5);
        break;
      }

      case 'table': {
        renderStyledTable(
          doc,
          writer,
          [el.headers],
          el.rows,
          { fontSize: 8 }
        );
        break;
      }
    }
  }
}

function getOverviewData(currentDiagram: Diagram, entities: Record<number, Entity>) {
  const stats = calculateDiagramStats(currentDiagram);
  const pathsWithoutAuth = currentDiagram.paths.filter((path) => !path.authentication).length;
  const pathsWithoutName = currentDiagram.paths.filter((path) => !path.name).length;
  const protocols = Array.from(new Set(currentDiagram.paths.map((path) => path.protocol).filter(Boolean)));

  const connections: Record<number, number> = {};
  currentDiagram.paths.forEach((path) => {
    connections[path.source_id] = (connections[path.source_id] || 0) + 1;
    connections[path.target_id] = (connections[path.target_id] || 0) + 1;
  });

  let mostConnectedEntityName = '';
  let mostConnectedCount = 0;
  Object.entries(connections).forEach(([entityId, count]) => {
    if (count > mostConnectedCount) {
      mostConnectedCount = count;
      mostConnectedEntityName = entities[Number(entityId)]?.name || 'Unknown';
    }
  });

  const entityTypeDistribution: Record<string, number> = {
    default: 0, external: 0, user: 0, process: 0, storage: 0,
  };
  currentDiagram.entities.forEach((entity) => {
    const t = entity.type || 'default';
    entityTypeDistribution[t] = (entityTypeDistribution[t] || 0) + 1;
  });

  return { stats, pathsWithoutAuth, pathsWithoutName, protocols, mostConnectedEntityName, mostConnectedCount, entityTypeDistribution };
}

// ─── Project Summary + Information Classification + Overview (combined page 1) ─

function writeFirstPage(writer: PdfWriter, params: ExportPdfParams, currentDiagram: Diagram, entitiesMap: Record<number, Entity>) {
  // ── Project Summary ──────────────────────────────────────────────────────
  writer.addSectionHeader('Project Summary', 'Ownership, metadata and information classification');

  const doc = writer.getDoc();
  const pi = params.projectInfo;

  // Two-column info layout
  const cw = writer.getContentWidth();
  const m = writer.getMargin();
  const colW = (cw - 4) / 2;

  // Left column: project identity
  const leftItems: Array<[string, string]> = [
    ['Project', pi ? pi.name : currentDiagram.name || 'N/A'],
    ['Department', pi?.department ? (typeof pi.department === 'string' ? pi.department : pi.department.shortname || pi.department.full_name || '—') : '—'],
  ];
  if (pi?.leanix_entries && pi.leanix_entries.length > 0) {
    pi.leanix_entries.forEach((entry, i) => {
      leftItems.push([
        i === 0 ? 'App ID' : '',
        entry.name_from_leanix ? `${entry.app_id} — ${entry.name_from_leanix}` : entry.app_id,
      ]);
    });
  }

  // Right column: contacts
  const rightItems: Array<[string, string]> = [
    ['Responsible ISO', pi?.responsible_iso || 'Not specified'],
    ['Technical Owner', pi?.technical_owner || 'Not specified'],
    ['Primary Contact', pi?.primary_point_of_contact || 'Not specified'],
  ];

  // Render two-column key-value blocks
  const maxRows = Math.max(leftItems.length, rightItems.length);
  const rowH = 8;
  const blockH = maxRows * rowH + 4;
  writer.ensureSpace(blockH);

  const startY = writer.getY();

  // Left block background
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(m, startY, colW, blockH, 1.5, 1.5, 'FD');

  // Right block background
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(m + colW + 4, startY, colW, blockH, 1.5, 1.5, 'FD');

  const renderKVBlock = (items: Array<[string, string]>, xBase: number) => {
    items.forEach(([key, value], i) => {
      const ry = startY + 4 + i * rowH;
      if (key) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...MUTED);
        doc.text(key.toUpperCase(), xBase + 3, ry);
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...BRAND);
      const vLines = doc.splitTextToSize(value, colW - 6) as string[];
      doc.text(vLines[0] || '-', xBase + 3, ry + 4.5);
    });
  };

  renderKVBlock(leftItems, m);
  renderKVBlock(rightItems, m + colW + 4);

  doc.setTextColor(0, 0, 0);
  writer.setY(startY + blockH + 3);

  // Description (if present) — subtle box
  const desc = pi?.description || currentDiagram.description;
  if (desc) {
    const descLines = doc.splitTextToSize(desc, cw - 8) as string[];
    const dh = Math.min(descLines.length, 4) * 4.5 + 8;
    writer.ensureSpace(dh + 2);
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(m, writer.getY(), cw, dh, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(51, 65, 85);
    for (let i = 0; i < Math.min(descLines.length, 4); i++) {
      doc.text(descLines[i], m + 4, writer.getY() + 5 + i * 4.5);
    }
    doc.setTextColor(0, 0, 0);
    writer.setY(writer.getY() + dh + 4);
  }

  // ── Information Classification ────────────────────────────────────────────
  writer.addSubsection('Information Classification');

  if (pi) {
    writer.addCiaCards([
      { label: 'Confidentiality', value: getCiaLabel(pi.confidentiality) },
      { label: 'Integrity',       value: getCiaLabel(pi.integrity) },
      { label: 'Availability',    value: getCiaLabel(pi.availability) },
      { label: 'Internet Facing', value: pi.internet_facing == null ? '—' : pi.internet_facing ? 'Yes' : 'No' },
    ]);
  } else {
    writer.addParagraph('No classification data available.');
  }

  // ── Overview ─────────────────────────────────────────────────────────────
  writer.addSubsection('Diagram Overview');

  const overview = getOverviewData(currentDiagram, entitiesMap);

  writer.addStatRow([
    { label: 'Entities',    value: String(overview.stats.entityCount),     bg: [219, 234, 254], fg: [30, 64, 175] },
    { label: 'Connections', value: String(overview.stats.pathCount),        bg: [220, 252, 231], fg: [22, 101, 52] },
    { label: 'Boundaries',  value: String(overview.stats.uniqueBoundaries), bg: [254, 243, 199], fg: [146, 64, 14] },
    { label: 'Protocols',   value: String(overview.stats.uniqueProtocols),  bg: [237, 233, 254], fg: [109, 40, 217] },
  ]);

  const authPct = overview.stats.pathCount > 0
    ? Math.round((1 - overview.pathsWithoutAuth / overview.stats.pathCount) * 100)
    : 0;
  writer.addBullet(`Authentication coverage: ${authPct}% (${overview.pathsWithoutAuth} connection(s) without auth)`,
    overview.pathsWithoutAuth > 0 ? [220, 38, 38] : [22, 163, 74]);
  writer.addBullet(`Unlabeled connections: ${overview.pathsWithoutName}`,
    overview.pathsWithoutName > 0 ? [234, 88, 12] : MUTED);
  writer.addBullet(`Protocols in use: ${overview.protocols.length > 0 ? overview.protocols.join(', ') : 'N/A'}`);

  if (overview.mostConnectedEntityName) {
    writer.addBullet(`Most connected entity: ${overview.mostConnectedEntityName} (${overview.mostConnectedCount} connection(s))`);
  }
  const dist = Object.entries(overview.entityTypeDistribution)
    .filter(([, c]) => c > 0)
    .map(([t, c]) => `${c} ${typeLabels[t] || t}`)
    .join(', ');
  writer.addBullet(`Entity distribution: ${dist || 'N/A'}`);
}

// ─── Entities ────────────────────────────────────────────────────────────────

function writeEntities(writer: PdfWriter, currentDiagram: Diagram, boundariesMap: Record<number, Boundary>) {
  writer.newSection('portrait');
  writer.addSectionHeader('Entities', `${currentDiagram.entities.length} entities in current diagram`);

  if (currentDiagram.entities.length === 0) {
    writer.addParagraph('No entities found.');
    return;
  }

  const entityRows = currentDiagram.entities.map((entity) => {
    const bc = getBoundaryChain(entity.boundary_id, boundariesMap) || '-';
    const scope = entity.type === 'external' ? 'Out of Scope' : 'In Scope';
    return [entity.name, typeLabels[entity.type] || entity.type, bc, scope, entity.description || '-', entityLabel(entity) || '-'];
  });

  renderStyledTable(
    writer.getDoc(), writer,
    [['Name', 'Type', 'Boundary', 'Scope', 'Description', 'Technical Info']],
    entityRows,
    {
      fontSize: 8,
      columnStyles: {
        0: { cellWidth: 30 }, 1: { cellWidth: 22 }, 2: { cellWidth: 38 },
        3: { cellWidth: 18 }, 4: { cellWidth: 38 }, 5: { cellWidth: 36 },
      },
    }
  );

  // Scope summary
  const inScope = currentDiagram.entities.filter((e) => e.type !== 'external').map((e) => e.name).sort();
  const outOfScope = currentDiagram.entities.filter((e) => e.type === 'external').map((e) => e.name).sort();

  writer.addSubsection('Systems Scope');
  const scopeRows: string[][] = [];
  const maxR = Math.max(inScope.length, outOfScope.length);
  for (let i = 0; i < maxR; i++) scopeRows.push([inScope[i] || '', outOfScope[i] || '']);

  renderStyledTable(
    writer.getDoc(), writer,
    [['Systems in Scope', 'Systems out of Scope']],
    scopeRows.length > 0 ? scopeRows : [['-', '-']],
    { fontSize: 8, headFill: [51, 65, 85] }
  );
}

// ─── Connections ─────────────────────────────────────────────────────────────

function writeConnections(
  writer: PdfWriter,
  currentDiagram: Diagram,
  entitiesMap: Record<number, Entity>,
  boundariesMap: Record<number, Boundary>
) {
  writer.newSection('landscape');
  writer.addSectionHeader('Connections', `${currentDiagram.paths.length} data flows in current diagram`);

  if (currentDiagram.paths.length === 0) {
    writer.addParagraph('No connections found.');
    return;
  }

  const doc = writer.getDoc();
  const pw = doc.internal.pageSize.getWidth();
  const m = writer.getMargin();
  const tableW = pw - m * 2;

  // Column widths summing to tableW (267 - 28 = 239mm for landscape A4)
  const colWidths: Record<number, { cellWidth: number }> = {
    0: { cellWidth: 7 },
    1: { cellWidth: 28 },
    2: { cellWidth: 15 },
    3: { cellWidth: 34 },
    4: { cellWidth: 28 },
    5: { cellWidth: 15 },
    6: { cellWidth: 34 },
    7: { cellWidth: 22 },
    8: { cellWidth: tableW - 7 - 28 - 15 - 34 - 28 - 15 - 34 - 22 - 16 - 22 },
    9: { cellWidth: 16 },
    10: { cellWidth: 22 },
  };

  const rows = currentDiagram.paths.map((path, idx) => {
    const src = entitiesMap[path.source_id];
    const tgt = entitiesMap[path.target_id];
    const authVal = path.authentication || '';
    const authDisplay = authVal ? authVal : 'None';
    return [
      String(idx + 1),
      src?.name || 'Unknown',
      src?.type || '-',
      getBoundaryChain(src?.boundary_id, boundariesMap) || '-',
      tgt?.name || 'Unknown',
      tgt?.type || '-',
      getBoundaryChain(tgt?.boundary_id, boundariesMap) || '-',
      path.name || '-',
      path.description || '-',
      path.protocol || '-',
      authDisplay,
    ];
  });

  autoTable(doc, {
    startY: writer.getY() + 1,
    head: [['#', 'Source', 'Src Type', 'Source Boundary', 'Target', 'Tgt Type', 'Target Boundary', 'Label', 'Description', 'Protocol', 'Authentication']],
    body: rows,
    margin: { left: m, right: m },
    theme: 'grid',
    tableWidth: tableW,
    styles: {
      fontSize: 7,
      cellPadding: 1.8,
      overflow: 'linebreak',
      valign: 'middle',
      lineColor: [203, 213, 225],
      lineWidth: 0.2,
      textColor: [30, 41, 59],
    },
    headStyles: {
      fillColor: BRAND,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: colWidths,
    didParseCell: (data) => {
      // Highlight rows missing authentication
      if (data.section === 'body' && data.column.index === 10) {
        const val = String(data.cell.raw || '');
        if (val === 'None') {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
      }
      // Color protocol column
      if (data.section === 'body' && data.column.index === 9) {
        data.cell.styles.textColor = [30, 64, 175];
        data.cell.styles.fontStyle = 'bold';
      }
      // Color source/target entity names
      if (data.section === 'body' && (data.column.index === 1 || data.column.index === 4)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = [30, 41, 59];
      }
    },
  });

  const finalY = (doc as JsPdfWithAutoTable).lastAutoTable?.finalY;
  writer.setY((finalY ?? writer.getY()) + 5);
}

// ─── Threats ─────────────────────────────────────────────────────────────────

function writeThreats(writer: PdfWriter, threats: Threat[]) {
  writer.newSection('landscape');
  writer.addSectionHeader('Threats', `${threats.length} identified security threats`);

  if (threats.length === 0) {
    writer.addParagraph('No threats have been identified for this project.');
    return;
  }

  const statusCounts = threats.reduce((acc, t) => {
    const k = t.status === 'mitigated' ? 'mitigated' : 'open';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const severityCounts = pdfSeverityCounts(threats);

  // Row 1: Status stats
  writer.addStatRow([
    { label: 'Total',     value: String(threats.length),               bg: [241, 245, 249], fg: BRAND },
    { label: 'Open',      value: String(statusCounts.open || 0),       bg: [254, 226, 226], fg: [153, 27, 27] },
    { label: 'Mitigated', value: String(statusCounts.mitigated || 0),  bg: [220, 252, 231], fg: [22, 101, 52] },
  ]);

  // Row 2: Severity stats
  writer.addStatRow([
    { label: 'Critical', value: String(severityCounts.critical || 0), bg: [254, 226, 226], fg: [153, 27, 27] },
    { label: 'High',     value: String(severityCounts.high || 0),     bg: [255, 237, 213], fg: [154, 52, 18] },
    { label: 'Medium',   value: String(severityCounts.medium || 0),   bg: [254, 243, 199], fg: [146, 64, 14] },
    { label: 'Low',      value: String(severityCounts.low || 0),      bg: [220, 252, 231], fg: [22, 101, 52] },
    { label: 'Note',     value: String(severityCounts.note || 0),     bg: [224, 242, 254], fg: [7, 89, 133] },
  ]);

  const doc = writer.getDoc();
  const pw = doc.internal.pageSize.getWidth();
  const m = writer.getMargin();
  const tableW = pw - m * 2;

  // Columns: #, Threat, Status, Severity, Vulnerability, Scenario, Countermeasure
  const colWidths: Record<number, { cellWidth: number }> = {
    0: { cellWidth: 7 },
    1: { cellWidth: 38 },
    2: { cellWidth: 20 },
    3: { cellWidth: 20 },
    4: { cellWidth: tableW - 7 - 38 - 20 - 20 - 46 - 46 },
    5: { cellWidth: 46 },
    6: { cellWidth: 46 },
  };

  const rows = threats.map((t, idx) => {
    const severityLabel = pdfSeverityLabel(t);
    return [
      String(idx + 1),
      t.name,
      t.status || 'open',
      severityLabel,
      t.vulnerability || '-',
      t.scenario || '-',
      t.countermeasure || '-',
    ];
  });

  autoTable(doc, {
    startY: writer.getY() + 1,
    head: [['#', 'Threat', 'Status', 'Severity', 'Vulnerability', 'Scenario', 'Countermeasure']],
    body: rows,
    margin: { left: m, right: m },
    theme: 'grid',
    tableWidth: tableW,
    styles: {
      fontSize: 7.5,
      cellPadding: 1.8,
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [203, 213, 225],
      lineWidth: 0.2,
      textColor: [30, 41, 59],
    },
    headStyles: {
      fillColor: BRAND,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: colWidths,
    didParseCell: (data) => {
      if (data.section === 'body') {
        // Status column: color by value
        if (data.column.index === 2) {
          const val = String(data.cell.raw || '').toLowerCase();
          if (val === 'mitigated') {
            data.cell.styles.textColor = [22, 101, 52];
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = [153, 27, 27];
            data.cell.styles.fontStyle = 'bold';
          }
        }
        // Severity column: color by level
        if (data.column.index === 3) {
          const raw = String(data.cell.raw || '').toUpperCase();
          if (raw.startsWith('CRIT')) data.cell.styles.textColor = [153, 27, 27];
          else if (raw.startsWith('HIGH')) data.cell.styles.textColor = [154, 52, 18];
          else if (raw.startsWith('MED')) data.cell.styles.textColor = [146, 64, 14];
          else if (raw.startsWith('LOW')) data.cell.styles.textColor = [22, 101, 52];
          else if (raw.startsWith('NOTE')) data.cell.styles.textColor = [7, 89, 133];
          else data.cell.styles.textColor = MUTED;
          data.cell.styles.fontStyle = 'bold';
        }
        // Threat name bold
        if (data.column.index === 1) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  const finalY = (doc as JsPdfWithAutoTable).lastAutoTable?.finalY;
  writer.setY((finalY ?? writer.getY()) + 5);
}

// ─── Notes (Markdown rendered element-by-element) ─────────────────────────────

async function writeNotes(writer: PdfWriter, currentDiagram: Diagram) {
  writer.newSection('portrait');
  writer.addSectionHeader('Notes', 'Diagram notes rendered from Markdown');

  const notes = currentDiagram.notes?.trim();
  if (!notes) {
    writer.addParagraph('No notes available for this diagram.');
    return;
  }

  try {
    const elements = await parseMarkdownElements(notes);
    await writeNotesElements(writer, elements);
  } catch (err) {
    console.error('Markdown parse failed:', err);
    notes.split('\n').forEach((line) => {
      writer.addParagraph(line.trim() === '' ? ' ' : line.trim());
    });
  }
}

export async function exportProjectPdf(params: ExportPdfParams) {
  if (!params.currentDiagram) {
    throw new Error('No diagram available to export.');
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const writer = new PdfWriter(doc);
  const entitiesMap = createEntityMap(params.currentDiagram);
  const boundariesMap = createBoundaryMap(params.currentDiagram);

  const exportDate = new Date().toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  const title = params.isProject && params.projectInfo
    ? `SPICE Report — ${params.projectInfo.name}`
    : `SPICE Report — ${params.currentDiagram.name || 'Diagram'}`;

  // Page 1: combined summary page (no cover page)
  writeFirstPage(writer, params, params.currentDiagram, entitiesMap);

  // Subsequent pages — each calls writer.newSection() at the start
  writeEntities(writer, params.currentDiagram, boundariesMap);
  writeConnections(writer, params.currentDiagram, entitiesMap, boundariesMap);
  writeThreats(writer, params.threats);
  await writeNotes(writer, params.currentDiagram);

  // Chrome on ALL pages (no cover page to skip anymore)
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pw, 11, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(title, 10, 7);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(exportDate, pw - 10, 7, { align: 'right' });

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.rect(0, ph - 9, pw, 9, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(`Page ${i} / ${totalPages}`, pw - 10, ph - 3.5, { align: 'right' });
    doc.text('SPICE', 10, ph - 3.5);
  }

  doc.setTextColor(0, 0, 0);

  const fileBase = params.isProject && params.projectInfo
    ? sanitizeFilename(params.projectInfo.name)
    : sanitizeFilename(params.currentDiagram.name || 'diagram');

  doc.save(`${fileBase}-spice-report.pdf`);
}
